import React, { useContext, useEffect, useRef, useState } from 'react'
import { CreationSourceEditorContext } from '../manager'
import styled from 'styled-components'
import StyledRangeSlider from '../../range-slider/styled-range-slider'
import { SourceEditorCaption } from '../types'
import Collapse from '../../collapse'
import { LayerTextIcon, LayerTextWhiteIcon } from '../../icons'
import { generateCaptionCanvas } from '../../artboard/utils'
import CreationToolFont from 'site/creation-tools/types/font'
import CreationToolFontSize from 'site/creation-tools/types/font-size'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import LayerOptionsMenu from '../../layer-options-menu'

const CANVAS_WIDTH = 75
const CANVAS_HEIGHT = 34

const Container = styled.div``

const LayerDescription = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-left: calc(12px + 16px);
    margin-right: calc(12px + 16px);
`

const TimeDuration = styled.div`
    font-size: 15px;
    font-weight: bold;
    span {
        opacity: 0.5;
    }
`

const Canvas = styled.canvas`
    margin-top: 2px;
    margin-right: 2px;
`

const LeftLayerDescription = styled.div`
    display: flex;
    align-items: center;
`

const RightLayerDescription = styled.div`
    display: flex;
    align-items: center;

    svg {
        height: 19px;
        margin-right: 5px;
        margin-top: -3px;
    }
`

type LayerItemProps = {
    caption: SourceEditorCaption
    index: number
}

const LayerItem = (props: LayerItemProps) => {
    const [isTrimming, setIsTrimming] = useState<boolean>(false)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const { caption, index } = props
    const { source, setCaptions, captions, duplicate, flip, sendToBack, toggleCaptionLayerOptions } = useContext(
        CreationSourceEditorContext
    )
    const { attributes, listeners, setNodeRef, transition, transform, isDragging } = useSortable({
        id: `${index}`,
    })

    const dragStyle = {
        transform: CSS.Translate.toString(transform),
        transition,
    }

    const startTime = caption.start || 0
    const endTime = caption.end || source.duration

    const onTimeUpdate = (start: number, end: number) => {
        setCaptions(
            captions.map((c, i) => {
                if (i === index) {
                    return { ...c, start: start, end: end }
                }
                return c
            })
        )
    }

    const drawPreviewText = async () => {
        if (canvasRef.current) {
            const context = canvasRef.current.getContext('2d')

            if (context) {
                const {
                    text,
                    style: { color, font, fontSize, strokeColor },
                } = caption

                await generateCaptionCanvas(
                    {
                        fontFamily: CreationToolFont.fontFamily(font),
                        fontSize: CreationToolFontSize.sizeBaseOnHeight(fontSize) * CreationToolFont.scale(font) * 150,
                        fontWeight: CreationToolFont.fontWeight(font),
                        color,
                        strokeColor,
                        lineHeight: 1.3,
                        text: (CreationToolFont.isAllCaps(font) ? text.toUpperCase() : text).substring(0, 9),
                    },
                    CANVAS_WIDTH,
                    canvasRef.current
                )
            }
        }
    }

    useEffect(() => {
        drawPreviewText()
    }, [canvasRef, caption.text])

    const selectedLayer = !!caption.options.isLayerOptionsOpened || isDragging || isTrimming

    return (
        <Container ref={setNodeRef} {...attributes} {...listeners} style={dragStyle}>
            <Collapse
                header={
                    <StyledRangeSlider
                        start={startTime / source.duration}
                        end={endTime / source.duration}
                        onUpdate={(start, end) => onTimeUpdate(start * source.duration, end * source.duration)}
                        customColor={'transparent'}
                        childrenAtMiddle
                        selected={selectedLayer}
                        onDragStart={() => setIsTrimming(true)}
                        onDragEnd={() => setIsTrimming(false)}
                    >
                        <LayerDescription>
                            <LeftLayerDescription>
                                <Canvas ref={canvasRef} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} />
                                {caption.text}
                            </LeftLayerDescription>
                            <RightLayerDescription>
                                {selectedLayer ? <LayerTextWhiteIcon /> : <LayerTextIcon />}
                                <TimeDuration>
                                    {Math.round(endTime - startTime)}s<span> / {Math.round(source.duration)}s</span>
                                </TimeDuration>
                            </RightLayerDescription>
                        </LayerDescription>
                    </StyledRangeSlider>
                }
                isOpened={!!caption.options.isLayerOptionsOpened}
                onChange={() => toggleCaptionLayerOptions(caption)}
            >
                <LayerOptionsMenu
                    source={source}
                    timerOptions={{
                        startTime,
                        endTime,
                        onTimeUpdate,
                    }}
                    duplicate={() => duplicate(caption)}
                    flip={() => flip(caption)}
                    sendToBack={() => sendToBack(caption)}
                />
            </Collapse>
        </Container>
    )
}

export default LayerItem
